import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { QualityTier, SettlementViewModel } from '@aztec-realms/shared-types';
import { useSceneStartup } from '../SceneStartup';

import {
  mountSettlementRenderer,
  type PlacementPreview,
  type SettlementCameraAction,
  type SettlementRendererController,
} from './settlement-renderer';

interface SettlementCanvasProps {
  snapshot: SettlementViewModel;
  selectedBuildingId?: string;
  placement?: PlacementPreview;
  editing?: boolean;
  cameraCommand?: { id: number; action: SettlementCameraAction };
  onPlacementChanged?: (x: number, y: number) => void;
  qualityTier: QualityTier;
  reducedMotion: boolean;
  onBuildingSelected: (buildingId: string) => void;
  onReady?: () => void;
  onUnavailable?: () => void;
  interactive?: boolean;
}

export function SettlementCanvas({
  snapshot,
  selectedBuildingId,
  placement,
  editing = false,
  cameraCommand,
  onPlacementChanged,
  qualityTier,
  reducedMotion,
  onBuildingSelected,
  onReady,
  onUnavailable,
  interactive = true,
}: SettlementCanvasProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<SettlementRendererController | null>(null);
  const initialSnapshotRef = useRef(snapshot);
  const initialSelectionRef = useRef(selectedBuildingId);
  const placementRef = useRef(placement);
  const editingRef = useRef(editing);
  const cameraCommandRef = useRef(cameraCommand);
  const appliedCommandRef = useRef<number | undefined>(undefined);
  const callbacksRef = useRef({
    interactive,
    onBuildingSelected,
    onPlacementChanged,
    onReady,
    onUnavailable,
  });
  const [status, setStatus] = useState<'loading' | 'ready' | 'fallback'>('loading');
  const [retry, setRetry] = useState(0);
  useSceneStartup(
    'home',
    status === 'ready',
    status === 'fallback'
      ? 'Your settlement could not load. Try again to return to your city.'
      : undefined,
  );

  useLayoutEffect(() => {
    callbacksRef.current = {
      interactive,
      onBuildingSelected,
      onPlacementChanged,
      onReady,
      onUnavailable,
    };
    initialSnapshotRef.current = snapshot;
    initialSelectionRef.current = selectedBuildingId;
    placementRef.current = placement;
    editingRef.current = editing;
    cameraCommandRef.current = cameraCommand;
  }, [
    interactive,
    onBuildingSelected,
    onPlacementChanged,
    onReady,
    onUnavailable,
    snapshot,
    selectedBuildingId,
    placement,
    editing,
    cameraCommand,
  ]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || navigator.userAgent.includes('jsdom')) {
      setStatus('fallback');
      callbacksRef.current.onUnavailable?.();
      return;
    }
    let cancelled = false;
    setStatus('loading');
    callbacksRef.current.onUnavailable?.();
    const abortController = new AbortController();
    void mountSettlementRenderer(host, initialSnapshotRef.current, {
      signal: abortController.signal,
      qualityTier,
      reducedMotion,
      selectedBuildingId: initialSelectionRef.current,
      placement: placementRef.current,
      editing: editingRef.current,
      onBuildingSelected: (buildingId) => {
        const callbacks = callbacksRef.current;
        if (!cancelled && callbacks.interactive && (!placementRef.current || editingRef.current))
          callbacks.onBuildingSelected(buildingId);
      },
      onPlacementChanged: (x, y) => {
        const callbacks = callbacksRef.current;
        if (!cancelled && callbacks.interactive && placementRef.current)
          callbacks.onPlacementChanged?.(x, y);
      },
      onReady: () => {
        if (cancelled) return;
        setStatus('ready');
        callbacksRef.current.onReady?.();
      },
      onArtUnavailable: () => {
        if (cancelled) return;
        setStatus('fallback');
        callbacksRef.current.onUnavailable?.();
      },
      onArtLoading: () => {
        if (cancelled) return;
        setStatus('loading');
        callbacksRef.current.onUnavailable?.();
      },
    })
      .then((controller) => {
        if (cancelled) controller.destroy();
        else {
          controllerRef.current = controller;
          // API state may have changed while Phaser was loading its textures.
          controller.update(initialSnapshotRef.current, initialSelectionRef.current);
          controller.setEditing(editingRef.current);
          controller.setPlacement(placementRef.current);
          const command = cameraCommandRef.current;
          if (command && callbacksRef.current.interactive && editingRef.current) {
            controller.commandCamera(command.action);
            appliedCommandRef.current = command.id;
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('fallback');
        callbacksRef.current.onUnavailable?.();
      });
    return () => {
      cancelled = true;
      abortController.abort();
      controllerRef.current?.destroy();
      controllerRef.current = null;
      host.replaceChildren();
    };
  }, [qualityTier, reducedMotion, retry]);

  useEffect(() => {
    initialSnapshotRef.current = snapshot;
    initialSelectionRef.current = selectedBuildingId;
    controllerRef.current?.update(snapshot, selectedBuildingId);
  }, [selectedBuildingId, snapshot]);

  useEffect(() => {
    controllerRef.current?.setEditing(editing);
  }, [editing]);

  useEffect(() => {
    controllerRef.current?.setPlacement(placement);
  }, [placement]);

  useEffect(() => {
    if (
      !cameraCommand ||
      !controllerRef.current ||
      !interactive ||
      !editing ||
      appliedCommandRef.current === cameraCommand.id
    )
      return;
    controllerRef.current.commandCamera(cameraCommand.action);
    appliedCommandRef.current = cameraCommand.id;
  }, [cameraCommand, editing, interactive]);

  return (
    <section aria-label={`${snapshot.name} interactive settlement`} className="settlement-canvas">
      <div
        aria-hidden="true"
        className="settlement-canvas__host"
        ref={hostRef}
        style={{ pointerEvents: interactive ? 'auto' : 'none', touchAction: 'none' }}
      />
      {status === 'loading' && (
        <span className="canvas-status">Preparing waterways and terraces…</span>
      )}
      {status === 'fallback' && (
        <div className="settlement-canvas__unavailable" role="status">
          <strong>Your settlement view could not load.</strong>
          <p>Your buildings are safe. Open Buildings to continue, or try loading the view again.</p>
          <button
            type="button"
            className="ar-button ar-button--secondary"
            onClick={() => setRetry((value) => value + 1)}
          >
            Reload settlement view
          </button>
        </div>
      )}
      <span className="ar-visually-hidden" role="status">
        {status === 'ready'
          ? 'Settlement scene ready.'
          : 'An accessible building list is available below.'}
      </span>
    </section>
  );
}
